import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Box } from '@mui/material';

interface Static2DAssetViewerProps {
    glbModelSrc: string;
    onClose: () => void;
}

export interface Static2DAssetViewerRef {
    handleClose: () => void;
}

const Static2DAssetViewer = forwardRef<Static2DAssetViewerRef, Static2DAssetViewerProps>(({ glbModelSrc, onClose }, ref) => {
    const [animationState, setAnimationState] = useState<'entering' | 'entered' | 'exiting'>('entering');

    useEffect(() => {
        if (animationState === 'entering') {
            const timer = setTimeout(() => {
                setAnimationState('entered');
            }, 100);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleClose = useCallback(() => {
        if (animationState !== 'exiting') {
            setAnimationState('exiting');
            setTimeout(() => {
                onClose();
            }, 800);
        }
    }, [onClose, animationState]);

    useImperativeHandle(ref, () => ({
        handleClose
    }), [handleClose]);

    const isVisible = animationState !== 'entering';
    const isExiting = animationState === 'exiting';

    return (
        <Box
            onClick={handleClose}
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                paddingTop: '2.5vh',
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                opacity: isExiting ? 0 : 1,
                transition: 'opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                zIndex: 10,
                cursor: 'pointer',
            }}
        >
            <Box
                component="img"
                src={glbModelSrc}
                alt="2D Asset"
                sx={{
                    maxWidth: '90%',
                    maxHeight: '84%',
                    objectFit: 'contain',
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible 
                        ? isExiting
                            ? 'translateY(-50px) scale(0.95)'
                            : 'translateY(0) scale(1)'
                        : 'translateY(50px) scale(0.95)',
                    transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                    pointerEvents: 'none',
                }}
            />
        </Box>
    );
});

export default Static2DAssetViewer;
